/**
 * VibeCoder - Application Entry Point
 *
 * This bootstraps the VibeCoder application with route-based code splitting.
 * Public routes (landing, legal, company pages) use a lightweight shell without
 * Firebase, billing, or authenticated user features.
 */

// Import Tailwind CSS
import './index.css';

import { createScopedLogger } from '@/core/utils/logger';

// Import React statically BEFORE Immer
// React must be loaded synchronously to initialize properly
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

// Enable Immer MapSet FIRST, before ANY other imports
// Zustand stores with Map/Set need this plugin enabled before they're created
import { enableMapSet } from 'immer';
enableMapSet();

// ============================================================================
// ROUTE-BASED APP SELECTION
// Determine which app shell to load based on current path BEFORE importing
// This prevents heavy authenticated app code from loading on public pages
// ============================================================================

/**
 * Public routes that should use the lightweight PublicApp shell.
 * These routes don't need Firebase, billing, or authenticated features.
 */
const PUBLIC_ROUTES = [
  '/',
  '/home',
  '/privacy',
  '/terms',
  '/rbac-policy',
  '/dpa',
  '/aup',
  '/cookies',
  '/cookie-preferences',
  '/ai-addendum',
  '/api-terms',
  '/dmca',
  '/subprocessors',
  '/payments',
  '/security',
  '/order-form',
  '/sla',
  '/security-disclosure',
  '/accessibility',
  '/support-terms',
  '/unsubscribe',
  '/about',
  '/careers',
  '/contact',
  '/press',
  '/support',
  '/community',
  '/changelog',
  '/roadmap',
  '/blog',
];

/**
 * Check if current path is a public route.
 */
function isPublicRoute(pathname: string): boolean {
  return PUBLIC_ROUTES.includes(pathname);
}

// Get current path
const currentPath = window.location.pathname;
const isPublic = isPublicRoute(currentPath);

// Debug logging to verify route detection (development only)
if (import.meta.env.DEV) {
  console.log('[Main] Current path:', currentPath);
  console.log('[Main] Is public route:', isPublic);
  console.log('[Main] Loading:', isPublic ? 'PublicApp' : 'Full App');
}

// ============================================================================
// PRECONNECT HINTS FOR AUTHENTICATED ROUTES
// Inject early connection hints for Firebase domains to reduce latency.
// Only added for non-public routes since public pages don't use Firebase.
// ============================================================================
if (!isPublic) {
  const preconnectDomains = [
    'https://firestore.googleapis.com',
    'https://identitytoolkit.googleapis.com',
    'https://securetoken.googleapis.com',
    'https://www.googleapis.com',
    'https://apis.google.com',
  ];

  preconnectDomains.forEach(domain => {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = domain;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  });

  // Also add dns-prefetch as fallback for browsers that don't support preconnect
  preconnectDomains.forEach(domain => {
    const link = document.createElement('link');
    link.rel = 'dns-prefetch';
    link.href = domain;
    document.head.appendChild(link);
  });

  if (import.meta.env.DEV) {
    console.log('[Main] Preconnect hints added for Firebase domains');
  }
}

// ============================================================================
// MAIN ENTRY POINT
// ============================================================================

async function main() {
  // Start overall app timing
  performance.mark('app-init-start');

  if (isPublic) {
    // ========================================================================
    // PUBLIC ROUTE - Load lightweight PublicApp
    // Only loads minimal dependencies needed for public pages
    // ========================================================================
    performance.mark('public-app-load-start');

    const [
      { default: PublicApp },
      { initializeI18n },
    ] = await Promise.all([
      import('./PublicApp'),
      import('./core/i18n/i18n'),
    ]);

    performance.mark('public-app-load-end');
    performance.measure('public-app-bundle-load', 'public-app-load-start', 'public-app-load-end');

    // Initialize i18n for translations
    performance.mark('i18n-init-start');
    await initializeI18n();
    performance.mark('i18n-init-end');
    performance.measure('i18n-initialization', 'i18n-init-start', 'i18n-init-end');

    // Render public app
    performance.mark('public-app-render-start');
    createRoot(document.getElementById('root')!).render(
      <StrictMode>
        <PublicApp />
      </StrictMode>
    );
    performance.mark('public-app-render-end');
    performance.measure('public-app-render', 'public-app-render-start', 'public-app-render-end');

    // Log overall timing
    performance.mark('app-init-end');
    performance.measure('total-app-initialization', 'app-init-start', 'app-init-end');
    if (import.meta.env.DEV) {
      console.log('[Perf] Public app initialized. Check Performance tab for detailed timing.');
    }
  } else {
    // ========================================================================
    // AUTHENTICATED ROUTE - Load full App with all features
    // ========================================================================
    performance.mark('full-app-load-start');

    const [
      { default: App },
      { initializeI18n },
      { useAISettingsStore },
      { registerAllScreenTemplates },
      { createScopedLogger },
    ] = await Promise.all([
      import('./App'),
      import('./core/i18n/i18n'),
      import('./core/ai/stores/aiSettingsStore'),
      import('./core/screen-templates'),
      import('./core/utils/logger'),
    ]);

    performance.mark('full-app-load-end');
    performance.measure('full-app-bundle-load', 'full-app-load-start', 'full-app-load-end');

    // Initialize i18n first (translations needed for UI)
    performance.mark('i18n-init-start');
    await initializeI18n();
    performance.mark('i18n-init-end');
    performance.measure('i18n-initialization', 'i18n-init-start', 'i18n-init-end');

    // Register screen templates
    registerAllScreenTemplates();

    // Initialize AI provider defaults (default models per capability)
    useAISettingsStore.getState().initializeDefaultProviders();

    // Log startup (non-blocking)
    const log = createScopedLogger('Main');
    log.debug('App initialized');

    // Render full app
    performance.mark('full-app-render-start');
    createRoot(document.getElementById('root')!).render(
      <StrictMode>
        <App />
      </StrictMode>
    );
    performance.mark('full-app-render-end');
    performance.measure('full-app-render', 'full-app-render-start', 'full-app-render-end');

    // Log overall timing
    performance.mark('app-init-end');
    performance.measure('total-app-initialization', 'app-init-start', 'app-init-end');
    if (import.meta.env.DEV) {
      console.log('[Perf] Full app initialized. Check Performance tab for detailed timing.');
    }
  }

  // Hide the loading screen now that React has mounted
  if (typeof window !== 'undefined' && (window as unknown as { __hideLoader?: () => void }).__hideLoader) {
    (window as unknown as { __hideLoader: () => void }).__hideLoader();
  }

  // ============================================================================
  // SERVICE WORKER REGISTRATION - Deferred to avoid blocking initial render
  // ============================================================================
  // Register after page is interactive (requestIdleCallback or 3s timeout)
  const registerSW = () => {
    // Dynamic import with type assertion for Vite PWA plugin virtual module
    import('virtual:pwa-register' as string).then((module: { registerSW: (options: {
      immediate?: boolean;
      onRegistered?: (registration: ServiceWorkerRegistration | undefined) => void;
      onRegisterError?: (error: Error) => void;
    }) => void }) => {
      module.registerSW({
        immediate: false,
        onRegistered(registration: ServiceWorkerRegistration | undefined) {
          if (registration && import.meta.env.DEV) {
             
            console.debug('[Main] Service worker registered');
          }
        },
        onRegisterError(error: Error) {
          const swLog = createScopedLogger('ServiceWorker');
          swLog.error('Registration failed', error);
        },
      });
    }).catch(() => {
      // PWA not available in dev mode - ignore
    });
  };

  if ('requestIdleCallback' in window) {
    (window as Window & { requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => void })
      .requestIdleCallback(registerSW, { timeout: 5000 });
  } else {
    setTimeout(registerSW, 3000);
  }

  // ============================================================================
  // WEB VITALS REPORTING - Non-blocking performance metrics
  // ============================================================================
  // Defer web-vitals to after page is fully interactive to minimize main-thread blocking
  const initWebVitals = () => {
    import('web-vitals').then(({ onCLS, onFCP, onLCP, onTTFB, onINP }) => {
      const reportMetric = (metric: { name: string; value: number; rating: string }) => {
        // Log metrics to console in development
        if (import.meta.env.DEV) {
          const color =
            metric.rating === 'good' ? 'green' : metric.rating === 'needs-improvement' ? 'orange' : 'red';
           
          console.log(`%c[Web Vitals] ${metric.name}: ${metric.value.toFixed(2)}`, `color: ${color}`);
        }

        // Production analytics disabled — uncomment when GA4 or custom endpoint is configured
        // if (import.meta.env.PROD) {
        //   sendToAnalytics(metric);
        // }
      };

      // Core Web Vitals
      onCLS(reportMetric); // Cumulative Layout Shift
      onLCP(reportMetric); // Largest Contentful Paint
      onINP(reportMetric); // Interaction to Next Paint (replaced FID)

      // Other metrics
      onFCP(reportMetric); // First Contentful Paint
      onTTFB(reportMetric); // Time to First Byte
    });
  };

  // Use requestIdleCallback to defer web-vitals until browser is idle
  if ('requestIdleCallback' in window) {
    (window as Window & { requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => void })
      .requestIdleCallback(initWebVitals, { timeout: 10000 });
  } else {
    setTimeout(initWebVitals, 5000);
  }
}

main();
